import React, { useState, useEffect } from 'react';
import { WhitelistAPI } from '../../utils/whitelistApi';
import { getToken } from '../../utils/auth';
import { toast } from 'react-hot-toast';
import { FaPlus, FaTrash, FaEnvelope, FaUpload, FaList, FaFileExcel, FaUser, FaPhone, FaBirthdayCake, FaVenusMars } from 'react-icons/fa';
import * as XLSX from 'xlsx';

interface WhitelistManagementProps {
    loadData: () => void;
}

const WhitelistManagement: React.FC<WhitelistManagementProps> = ({ loadData }) => {
    const [whitelist, setWhitelist] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);

    // Add Modal State
    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');
    const [notes, setNotes] = useState('');

    // Bulk Import State
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);

    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; email: string | null }>({
        isOpen: false,
        email: null
    });

    useEffect(() => {
        loadWhitelist();
    }, []);

    const loadWhitelist = async () => {
        const token = getToken();
        if (!token) return;

        try {
            setLoading(true);
            const data = await WhitelistAPI.getWhitelist(token);
            setWhitelist(data.whitelist);
        } catch (error) {
            console.error('Error loading whitelist:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = getToken();
        if (!token) return;

        try {
            await WhitelistAPI.addEmail({ email: newEmail, name: newName, notes }, token);
            toast.success('Đã thêm email vào whitelist');
            setShowAddModal(false);
            setNewEmail('');
            setNewName('');
            setNotes('');
            loadWhitelist();
        } catch (error: any) {
            console.error('Error adding email:', error);
            toast.error(error.message || 'Không thể thêm email');
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setExcelFile(file);
            readExcel(file);
        }
    };

    const readExcel = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Read as array of arrays

            if (jsonData.length < 2) {
                toast.error('File không có dữ liệu');
                return;
            }

            // Map columns based on header row (assumed row 0)
            const headers: any = jsonData[0];
            const mapIndexes: any = {};

            headers.forEach((h: any, i: number) => {
                const header = String(h).toLowerCase().trim();
                if (header.includes('họ') && header.includes('tên')) mapIndexes.name = i;
                else if (header.includes('sinh')) mapIndexes.dob = i;
                else if (header.includes('tính')) mapIndexes.gender = i;
                else if (header.includes('email')) mapIndexes.email = i;
                else if (header.includes('sđt') || header.includes('điện thoại')) mapIndexes.phone = i;
            });

            if (mapIndexes.email === undefined) {
                toast.error('Không tìm thấy cột Email');
                return;
            }

            const parsedItems = [];
            // Start from row 1
            for (let i = 1; i < jsonData.length; i++) {
                const row: any = jsonData[i];
                if (!row || row.length === 0) continue;

                const email = row[mapIndexes.email];
                if (!email) continue;

                const name = mapIndexes.name !== undefined ? row[mapIndexes.name] : null;

                // Parse DOB
                let dob = null;
                const rawDob = mapIndexes.dob !== undefined ? row[mapIndexes.dob] : null;
                if (rawDob) {
                    // Handle Excel serial date or string
                    if (typeof rawDob === 'number') {
                        const date = new Date(Math.round((rawDob - 25569) * 86400 * 1000));
                        dob = date.toISOString().split('T')[0];
                    } else if (typeof rawDob === 'string') {
                        // Try DD/MM/YYYY
                        const parts = rawDob.split('/');
                        if (parts.length === 3) dob = `${parts[2]}-${parts[1]}-${parts[0]}`;
                        else dob = rawDob; // Keep as is if format unknown, or try date parse
                    }
                }

                // Parse Gender
                let gender = null;
                const rawGender = mapIndexes.gender !== undefined ? String(row[mapIndexes.gender]).toLowerCase().trim() : '';
                if (rawGender === 'nam' || rawGender === 'male') gender = 'male';
                else if (rawGender === 'nữ' || rawGender === 'female' || rawGender === 'nu') gender = 'female';
                else if (rawGender) gender = 'other';

                const phone = mapIndexes.phone !== undefined ? String(row[mapIndexes.phone]).replace(/\s/g, '') : null;

                parsedItems.push({ email, name, dob, gender, phone });
            }

            setPreviewData(parsedItems);
        };
        reader.readAsBinaryString(file);
    };

    const handleBulkImport = async () => {
        const token = getToken();
        if (!token) return;
        if (previewData.length === 0) {
            toast.error('Không có dữ liệu để import');
            return;
        }

        setIsProcessing(true);
        try {
            const result = await WhitelistAPI.bulkAddEmails(previewData, token);

            if (result.added > 0) {
                toast.success(`Đã thêm ${result.added} thành viên`);
            }
            if (result.skipped > 0) {
                toast('Một số thành viên đã tồn tại hoặc lỗi', { icon: '⚠️' });
            }

            setShowBulkModal(false);
            setExcelFile(null);
            setPreviewData([]);
            loadWhitelist();
        } catch (error: any) {
            console.error('Error bulk adding:', error);
            toast.error(error.message || 'Lỗi import');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRemoveEmail = (email: string) => {
        setDeleteModal({ isOpen: true, email });
    };

    const confirmDelete = async () => {
        if (!deleteModal.email) return;

        const token = getToken();
        if (!token) return;

        try {
            await WhitelistAPI.removeEmail(deleteModal.email, token);
            setDeleteModal({ isOpen: false, email: null });
            loadWhitelist();
        } catch (error) {
            loadWhitelist();
            console.error('Error removing email:', error);
            setDeleteModal({ isOpen: false, email: null });
        }
    };

    if (loading) {
        return <div className="text-center py-8">Đang tải...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email Whitelist</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
                        Chỉ email trong danh sách này mới được phép đăng ký
                    </p>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <button
                        onClick={() => setShowBulkModal(true)}
                        className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all text-sm"
                    >
                        <FaUpload /> <span className="hidden sm:inline">Import Excel</span><span className="sm:hidden">Import</span>
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-all text-sm"
                    >
                        <FaPlus /> Thêm <span className="hidden sm:inline">Thủ công</span>
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-blue-900 dark:text-blue-100 font-semibold flex items-center gap-2">
                    <FaList className="text-blue-500" /> Tổng số: <span className="text-2xl">{whitelist.length}</span>
                </p>
            </div>

            {/* Desktop Table View */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Thành viên</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Thông tin</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ngày thêm</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {whitelist.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                                <FaUser className="text-gray-400 text-xs" />
                                                {item.name || 'Chưa có tên'}
                                            </span>
                                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1">
                                                <FaEnvelope className="text-gray-400 text-xs" />
                                                {item.email}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col text-sm text-gray-500 dark:text-gray-400 gap-1">
                                            {item.phone && <div className="flex items-center gap-2"><FaPhone className="text-xs" /> {item.phone}</div>}
                                            {item.dob && <div className="flex items-center gap-2"><FaBirthdayCake className="text-xs" /> {new Date(item.dob).toLocaleDateString('vi-VN')}</div>}
                                            {item.gender && <div className="flex items-center gap-2"><FaVenusMars className="text-xs" /> {item.gender === 'male' ? 'Nam' : item.gender === 'female' ? 'Nữ' : 'Khác'}</div>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(item.added_at).toLocaleDateString('vi-VN')}
                                        <div className="text-xs text-gray-400 mt-1">bởi {item.added_by_name || 'Admin'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                        <button
                                            onClick={() => handleRemoveEmail(item.email)}
                                            className="text-red-600 hover:text-red-900 dark:hover:text-red-400"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowAddModal(false);
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Thêm Email</h3>
                        <form onSubmit={handleAddEmail} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Email *</label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg dark:text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Họ và tên</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ghi chú</label>
                                <input
                                    type="text"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg dark:text-white"
                                />
                            </div>
                            <div className="flex justify-end gap-3 mt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Hủy</button>
                                <button type="submit" className="px-4 py-2 text-white bg-emerald-600 rounded-lg">Thêm</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Import Modal */}
            {showBulkModal && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowBulkModal(false);
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full p-6">
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Import Excel</h3>

                        {!excelFile ? (
                            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors relative">
                                <input
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileUpload}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <FaFileExcel className="w-12 h-12 text-green-600 mx-auto mb-3" />
                                <p className="text-gray-600 dark:text-gray-300 font-medium">Kéo thả file Excel hoặc click để chọn</p>
                                <p className="text-sm text-gray-500 mt-2">Hỗ trợ .xlsx, .xls (Cột: STT, Họ và tên, Ngày sinh, Giới tính, Email, SĐT)</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                                    <div className="flex items-center gap-3">
                                        <FaFileExcel className="text-green-600" />
                                        <span className="font-medium text-gray-900 dark:text-white">{excelFile.name}</span>
                                    </div>
                                    <button onClick={() => { setExcelFile(null); setPreviewData([]); }} className="text-red-500 text-sm hover:underline">Thay đổi</button>
                                </div>

                                <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left">Tên</th>
                                                <th className="px-4 py-2 text-left">Email</th>
                                                <th className="px-4 py-2 text-left">SĐT</th>
                                                <th className="px-4 py-2 text-left">Ngày sinh</th>
                                                <th className="px-4 py-2 text-left">GT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.map((row, idx) => (
                                                <tr key={idx} className="border-t border-gray-100 dark:border-gray-700">
                                                    <td className="px-4 py-2">{row.name}</td>
                                                    <td className="px-4 py-2">{row.email}</td>
                                                    <td className="px-4 py-2">{row.phone}</td>
                                                    <td className="px-4 py-2">{row.dob}</td>
                                                    <td className="px-4 py-2">{row.gender}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button onClick={() => { setShowBulkModal(false); setExcelFile(null); }} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Hủy</button>
                                    <button
                                        onClick={handleBulkImport}
                                        disabled={isProcessing}
                                        className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {isProcessing ? 'Đang xử lý...' : `Import ${previewData.length} dòng`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {deleteModal.isOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setDeleteModal({ isOpen: false, email: null });
                    }}
                >
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
                        <FaTrash className="text-4xl text-red-500 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2 dark:text-white">Xác nhận xóa?</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">Xóa {deleteModal.email} khỏi whitelist?</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteModal({ isOpen: false, email: null })} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">Hủy</button>
                            <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg">Xóa</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default WhitelistManagement;
